import {Component, type ErrorInfo, type ReactNode} from 'react';
import {toError} from '../utils/toError';
import {Notice} from './notice';
import {Typography} from './text';

/**
 * Catches a render-time throw so one broken screen does not blank the application.
 *
 * Without one, any error thrown while rendering unmounts the whole tree and leaves an empty document —
 * which, with skeletons rather than spinners for loading, is exactly what a loading screen looks like.
 * "It's still loading" and "it crashed" would be the same picture.
 *
 * A class, because `componentDidCatch` has no hook equivalent. This is the one place in the app where a
 * class component is the correct tool rather than a leftover.
 *
 * Deliberately no retry button. Every screen behind this boundary gets its data from React Query, which
 * already retries and already surfaces request failures through ErrorNotice — so anything reaching here is a
 * bug in rendering, not a transient fault, and a button that re-renders the same broken tree would just fail
 * again while implying the user did something wrong.
 */
type Props = {
    children: ReactNode;
    /**
     * Changing this clears a caught error.
     *
     * The caller passes the current path. Without it the boundary latches: once any route threw, it would
     * keep rendering the fallback for every later navigation until a full reload — while the fallback text
     * tells the user to go somewhere else, which could not work.
     *
     * A prop compared in componentDidUpdate rather than a `key` on the boundary, deliberately. A key would
     * remount this subtree on EVERY navigation, discarding component state across ordinary route changes.
     * This resets only when an error is actually being shown.
     */
    resetKey: string;
};
type State = {error: Error | null};

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {error: null};

    /**
     * Takes `unknown`, not `Error`. React types this parameter as Error, but `throw` accepts any value, so
     * the coercion has to happen before anything reads `.message`.
     */
    public static getDerivedStateFromError(thrown: unknown): State {
        return {error: toError(thrown)};
    }

    public componentDidUpdate(previous: Props): void {
        if (this.state.error && previous.resetKey !== this.props.resetKey) {
            this.setState({error: null});
        }
    }

    public componentDidCatch(thrown: unknown, info: ErrorInfo): void {
        /**
         * Logged to the console rather than sent anywhere. There is no client error reporting on this
         * deployment, and the component stack is the only thing that makes a minified render error
         * diagnosable — so it needs to be somewhere a person can reach it, which for a VPN-internal tool
         * means devtools.
         */
        console.error('Unhandled render error', toError(thrown), info.componentStack);
    }

    public render(): ReactNode {
        const {error} = this.state;

        if (!error) return this.props.children;

        return (
            <Notice tone="error" message="This screen failed to render">
                <Typography.Text size="small">
                    Navigating anywhere else clears this. If it keeps happening it is a bug worth
                    reporting, and the browser console has the component stack.
                </Typography.Text>
                <pre className="bt-error-detail">{error.message}</pre>
            </Notice>
        );
    }
}
